import { auth, db } from "../../firebase";
import { 
  createUserWithEmailAndPassword,
  sendEmailVerification
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

/**
 * Inscription d'un nouvel utilisateur (agriculteur ou acheteur)
 * @param {Object} userData - Données de l'utilisateur
 * @param {string} userData.userType - Type d'utilisateur ("buyer" ou "seller")
 * @param {string} userData.email - Email de l'utilisateur
 * @param {string} userData.password - Mot de passe
 * @param {string} userData.fullName - Nom complet
 * @param {string} userData.phone - Numéro de téléphone
 * @param {string} userData.province - Province
 * @param {string} userData.farmLocation - Localisation (adresse pour acheteur, ferme pour vendeur)
 * @param {string} userData.farmName - Nom de la ferme (vendeur uniquement)
 * @param {string} userData.crops - Cultures principales (vendeur uniquement)
 * @param {string} userData.farmType - Type de ferme (vendeur uniquement)
 * @param {string} userData.expertise - Expertise de l'agriculteur (vendeur uniquement)
 * @param {string} userData.cooperativeName - Nom de la coopérative (vendeur uniquement)
 * @param {boolean} userData.isCooperativeMember - Membre d'une coopérative (vendeur uniquement)
 * @param {boolean} userData.hasExpertise - A une expertise (vendeur uniquement)
 * @param {boolean} userData.hasPlot - A un terrain (vendeur uniquement)
 * @param {number} userData.plotSize - Taille du terrain (vendeur uniquement)
 * @returns {Promise<Object>} - L'utilisateur créé
 */
export const registerFarmer = async (userData) => {
  try {
    // 1. Créer l'utilisateur avec Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );
    const user = userCredential.user;

    // Déterminer la collection en fonction du type d'utilisateur
    const collectionName = userData.userType === "buyer" ? "buyers" : "farmers";
    
    // 2. Préparer les données de base communes aux deux types d'utilisateurs
    const baseUserData = {
      // Informations de base
      name: userData.fullName,
      email: userData.email,
      phoneNumber: userData.phone,
      province: userData.province || "",
      
      // Métadonnées
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      userId: user.uid,
      role: userData.userType === "buyer" ? "buyer" : "farmer",
      isVerified: false,
      emailVerified: false,
    };
    
    // 3. Ajouter les données spécifiques selon le type d'utilisateur
    let finalUserData;
    
    if (userData.userType === "buyer") {
      // Données spécifiques aux acheteurs
      finalUserData = {
        ...baseUserData,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        birthDate: userData.birthDate || "",
        address: userData.farmLocation || "", // Utiliser farmLocation comme adresse pour les acheteurs
        adminApproved: true, // Les acheteurs sont automatiquement approuvés
        status: "approved", // Statut approuvé par défaut
        profileCompleted: true
      };
    } else {
      // Données spécifiques aux vendeurs (agriculteurs)
      finalUserData = {
        ...baseUserData,
        // Informations de la ferme
        farmName: userData.farmName,
        location: userData.farmLocation,
        crops: userData.crops || "",
        farmType: userData.farmType || "maraichage",
        
        // Informations coopérative
        cooperativeName: userData.cooperativeName || "",
        isCooperativeMember: userData.isCooperativeMember || "non",
        cooperativeMembers: userData.cooperativeMembers || "0",
        
        // Expertise et terrain
        expertise: userData.expertise || "",
        hasExpertise: userData.hasExpertise || "non",
        hasPlot: userData.hasPlot || "non",
        plotSize: userData.plotSize || 0,
        
        // Statut spécifique aux vendeurs
        adminApproved: false,
        status: "pending", // Statut par défaut: en attente d'approbation
        profileCompleted: false
      };
    }

    // 4. Stocker les informations dans Firestore
    await setDoc(doc(db, collectionName, user.uid), finalUserData);

    // 5. Envoyer un email de vérification
    await sendEmailVerification(user, {
      url: `${window.location.origin}/email-verification`,
    });

    return {
      success: true,
      user,
      message: "Inscription réussie. Veuillez vérifier votre email."
    };
  } catch (error) {
    console.error("Erreur lors de l'inscription:", error);
    
    let errorMessage = "Une erreur est survenue lors de l'inscription";
    
    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage = "Cet email est déjà utilisé";
        break;
      case "auth/invalid-email":
        errorMessage = "Format d'email invalide";
        break;
      case "auth/weak-password":
        errorMessage = "Le mot de passe est trop faible";
        break;
      case "auth/network-request-failed":
        errorMessage = "Problème de connexion réseau";
        break;
      default:
        errorMessage = `Erreur: ${error.message}`;
    }
    
    return {
      success: false,
      error: error.code,
      message: errorMessage
    };
  }
};

/**
 * Vérifie si un utilisateur est un agriculteur ou un acheteur enregistré
 * @param {string} userId - ID de l'utilisateur à vérifier
 * @param {string} userType - Type d'utilisateur ("buyer" ou "farmer")
 * @returns {Promise<boolean>} - true si l'utilisateur existe dans la collection correspondante
 */
export const checkIsFarmer = async (userId, userType = "farmer") => {
  try {
    const collectionName = userType === "buyer" ? "buyers" : "farmers";
    const userDoc = await getDoc(doc(db, collectionName, userId));
    return userDoc.exists();
  } catch (error) {
    console.error("Erreur lors de la vérification du statut d'utilisateur:", error);
    return false;
  }
};

/**
 * Met à jour le statut de vérification de l'utilisateur après vérification de l'email
 * @param {string} userId - ID de l'utilisateur
 * @param {string} userType - Type d'utilisateur ("buyer" ou "farmer")
 * @returns {Promise<Object>} - Résultat de la mise à jour
 */
export const updateFarmerVerificationStatus = async (userId, userType = "farmer") => {
  try {
    const user = auth.currentUser;
    const collectionName = userType === "buyer" ? "buyers" : "farmers";
    
    if (user && user.emailVerified) {
      const updateData = { 
        isVerified: true, 
        emailVerified: true,
        updatedAt: serverTimestamp() 
      };
      
      await setDoc(
        doc(db, collectionName, userId),
        updateData,
        { merge: true }
      );
      
      // Message différent selon le type d'utilisateur
      const message = userType === "buyer" 
        ? "Votre compte a été vérifié avec succès. Vous pouvez maintenant utiliser la plateforme."
        : "Statut de vérification mis à jour avec succès. Votre compte est en attente d'approbation par un administrateur.";
      
      return {
        success: true,
        message
      };
    }
    
    return {
      success: false,
      message: "L'email n'est pas encore vérifié"
    };
  } catch (error) {
    console.error("Erreur lors de la mise à jour du statut de vérification:", error);
    return {
      success: false,
      error: error.code || error.message,
      message: "Erreur lors de la mise à jour du statut de vérification"
    };
  }
};

/**
 * Vérifie si un utilisateur est approuvé par un administrateur
 * @param {string} userId - ID de l'utilisateur
 * @param {string} userType - Type d'utilisateur ("buyer" ou "farmer")
 * @returns {Promise<boolean>} - true si l'utilisateur est approuvé
 */
export const checkFarmerApprovalStatus = async (userId, userType = "farmer") => {
  try {
    const collectionName = userType === "buyer" ? "buyers" : "farmers";
    const userDoc = await getDoc(doc(db, collectionName, userId));
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    
    // Les acheteurs sont toujours considérés comme approuvés
    if (userType === "buyer") {
      return true;
    }
    
    return userData.adminApproved === true;
  } catch (error) {
    console.error("Erreur lors de la vérification du statut d'approbation:", error);
    return false;
  }
};